import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

// Main component for the Image Gallery
export function ImageGallery({ images = [] }) {
  const [opened, setOpened] = useState(0);
  const [inPlace, setInPlace] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [gsapReady, setGsapReady] = useState(false);
  const autoplayTimer = useRef(null);

  useEffect(() => {
    // This effect loads the GSAP library and its plugin from a CDN.
    const loadScripts = () => {
      if (window.gsap && window.MotionPathPlugin) {
        window.gsap.registerPlugin(window.MotionPathPlugin);
        setGsapReady(true);
        return;
      }
      
      const gsapScript = document.createElement("script");
      gsapScript.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js";
      gsapScript.onload = () => {
        const motionPathScript = document.createElement("script");
        motionPathScript.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/MotionPathPlugin.min.js";
        motionPathScript.onload = () => {
          if (window.gsap && window.MotionPathPlugin) {
            window.gsap.registerPlugin(window.MotionPathPlugin);
            setGsapReady(true);
          }
        };
        document.body.appendChild(motionPathScript);
      };
      document.body.appendChild(gsapScript);
    };

    loadScripts();
  }, []);

  const onClick = (index) => {
    if (!disabled) setOpened(index);
  };

  const onInPlace = (index) => setInPlace(index);

  const next = useCallback(() => {
    setOpened((currentOpened) => {
      let nextIndex = currentOpened + 1;
      if (nextIndex >= images.length) nextIndex = 0;
      return nextIndex;
    });
  }, [images.length]);

  const prev = useCallback(() => {
    setOpened((currentOpened) => {
      let prevIndex = currentOpened - 1;
      if (prevIndex < 0) prevIndex = images.length - 1;
      return prevIndex;
    });
  }, [images.length]);

  // Disable clicks during animation transitions
  useEffect(() => setDisabled(true), [opened]);
  useEffect(() => setDisabled(false), [inPlace]);

  // Autoplay and timer reset logic
  useEffect(() => {
    if (!gsapReady) return;
    
    if (autoplayTimer.current) {
      clearInterval(autoplayTimer.current);
    }
    
    autoplayTimer.current = window.setInterval(next, 4500);

    return () => {
      if (autoplayTimer.current) {
        clearInterval(autoplayTimer.current);
      }
    };
  }, [opened, gsapReady, next]);

  if (!images || images.length === 0) return null;

  return (
    <div className="relative w-full max-w-4xl mx-auto flex items-center justify-center font-sans">
      
      {/* Navigation Arrows */}
      <button 
        className="absolute -left-4 md:-left-12 lg:-left-20 top-1/2 z-[101] flex h-12 w-12 sm:h-16 sm:w-16 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-brand-darkSage/10 bg-white/95 backdrop-blur-sm shadow-[0_8px_32px_rgba(0,0,0,0.08)] outline-none transition-all duration-300 ease-out hover:scale-110 hover:bg-white hover:border-brand-gold/50 hover:text-brand-gold text-brand-darkSage active:scale-95 focus-visible:ring-4 focus-visible:ring-brand-gold/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        onClick={prev}
        disabled={disabled}
        aria-label="Previous Image"
      >
        <FiChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 transition-transform duration-300 group-hover:-translate-x-0.5" />
      </button>
      
      <button 
        className="absolute -right-4 md:-right-12 lg:-right-20 top-1/2 z-[101] flex h-12 w-12 sm:h-16 sm:w-16 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-brand-darkSage/10 bg-white/95 backdrop-blur-sm shadow-[0_8px_32px_rgba(0,0,0,0.08)] outline-none transition-all duration-300 ease-out hover:scale-110 hover:bg-white hover:border-brand-gold/50 hover:text-brand-gold text-brand-darkSage active:scale-95 focus-visible:ring-4 focus-visible:ring-brand-gold/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        onClick={next}
        disabled={disabled}
        aria-label="Next Image"
      >
        <FiChevronRight className="w-6 h-6 sm:w-8 sm:h-8 transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>

      {/* Main Gallery Container */}
      <div className="relative aspect-square w-full max-w-[600px] overflow-hidden rounded-[2rem] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.1)] border-8 border-white/50 bg-white">
        {gsapReady && images.map((image, i) => (
          <div
            key={image.url}
            className="absolute left-0 top-0 h-full w-full"
            style={{ zIndex: inPlace === i ? i : images.length + 1 }}
          >
            <GalleryImage 
              total={images.length} 
              id={i} 
              url={image.url} 
              title={image.title} 
              open={opened === i} 
              inPlace={inPlace === i} 
              onInPlace={onInPlace} 
            />
          </div>
        ))}
        <div className="absolute left-0 top-0 z-[100] h-full w-full pointer-events-none">
          <Tabs images={images} onSelect={onClick} />
        </div>
      </div>
    </div>
  );
}

function GalleryImage({ url, title, open, inPlace, id, onInPlace, total }) {
  const [firstLoad, setLoaded] = useState(true);
  const clip = useRef(null);

  // --- Animation Constants ---
  const gap = 16;
  const circleRadius = 14;
  const defaults = { transformOrigin: "center center" };
  const duration = 0.4;
  const width = 400;
  const height = 400;
  const scale = 700;
  const bigSize = circleRadius * scale;
  const overlap = 0;

  // --- Position Calculation Functions ---
  const getPosSmall = () => ({
    cx: width / 2 - (total * (circleRadius * 2 + gap) - gap) / 2 + id * (circleRadius * 2 + gap),
    cy: height - 40,
    r: circleRadius,
  });
  const getPosSmallAbove = () => ({
    cx: width / 2 - (total * (circleRadius * 2 + gap) - gap) / 2 + id * (circleRadius * 2 + gap),
    cy: height / 2,
    r: circleRadius * 2,
  });
  const getPosCenter = () => ({
    cx: width / 2,
    cy: height / 2,
    r: circleRadius * 7
  });
  const getPosEnd = () => ({
    cx: width / 2 - bigSize + overlap,
    cy: height / 2,
    r: bigSize
  });
  const getPosStart = () => ({
    cx: width / 2 + bigSize - overlap,
    cy: height / 2,
    r: bigSize
  });

  // --- Animation Logic ---
  useEffect(() => {
    const gsap = window.gsap;
    if (!gsap) return; // Guard against GSAP not being loaded yet
    
    setLoaded(false);
    
    if (clip.current) {
      const flipDuration = firstLoad ? 0 : duration;
      const upDuration = firstLoad ? 0 : 0.2;
      const bounceDuration = firstLoad ? 0.01 : 1;
      const delay = firstLoad ? 0 : flipDuration + upDuration;

      if (open) {
        gsap
          .timeline()
          .set(clip.current, { ...defaults, ...getPosSmall() })
          .to(clip.current, {
            ...defaults,
            ...getPosCenter(),
            duration: upDuration,
            ease: "power3.inOut",
          })
          .to(clip.current, {
            ...defaults,
            ...getPosEnd(),
            duration: flipDuration,
            ease: "power4.in",
            onComplete: () => onInPlace(id),
          });
      } else {
        gsap
          .timeline({ overwrite: true })
          .set(clip.current, { ...defaults, ...getPosStart() })
          .to(clip.current, {
            ...defaults,
            ...getPosCenter(),
            delay: delay,
            duration: flipDuration,
            ease: "power4.out",
          })
          .to(clip.current, {
            ...defaults,
            motionPath: {
              path: [getPosSmallAbove(), getPosSmall()],
              curviness: 1,
            },
            duration: bounceDuration,
            ease: "bounce.out",
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      xmlnsXlink="http://www.w3.org/1999/xlink" 
      viewBox={`0 0 ${width} ${height}`} 
      preserveAspectRatio="xMidYMid slice" 
      className="h-full w-full drop-shadow-xl"
    >
      <defs>
        <clipPath id={`${id}_circleClip`}>
          <circle className="clip" cx="0" cy="0" r={circleRadius} ref={clip}></circle>
        </clipPath>
        <clipPath id={`${id}_squareClip`}>
          <rect className="clip" width={width} height={height}></rect>
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}${inPlace ? "_squareClip" : "_circleClip"})`}>
        {/* Solid white background so transparent PNGs don't blend with images underneath */}
        <rect width={width} height={height} fill="#ffffff" />
        <image
          width={width}
          height={height}
          href={url}
          className="pointer-events-none"
          preserveAspectRatio="xMidYMid slice"
        />
      </g>
    </svg>
  );
}

function Tabs({ images, onSelect }) {
  const gap = 16;
  const circleRadius = 14;
  const width = 400;
  const height = 400;

  const getPosX = (i) => width / 2 - (images.length * (circleRadius * 2 + gap) - gap) / 2 + i * (circleRadius * 2 + gap);
  const getPosY = () => height - 40;

  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      xmlnsXlink="http://www.w3.org/1999/xlink" 
      viewBox={`0 0 ${width} ${height}`} 
      preserveAspectRatio="xMidYMid slice" 
      className="h-full w-full"
    >
      {images.map((image, i) => (
        <g key={image.url} className="pointer-events-auto">
          <defs>
            <clipPath id={`tab_${i}_clip`}>
              <circle cx={getPosX(i)} cy={getPosY()} r={circleRadius} />
            </clipPath>
          </defs>
          <rect
            x={getPosX(i) - circleRadius}
            y={getPosY() - circleRadius}
            width={circleRadius * 2}
            height={circleRadius * 2}
            fill="#ffffff"
            clipPath={`url(#tab_${i}_clip)`}
            className="pointer-events-none"
          />
          <image
            x={getPosX(i) - circleRadius}
            y={getPosY() - circleRadius}
            width={circleRadius * 2}
            height={circleRadius * 2}
            href={image.url}
            clipPath={`url(#tab_${i}_clip)`}
            className="pointer-events-none"
            preserveAspectRatio="xMidYMid slice"
          />
          <circle 
            onClick={() => onSelect(i)} 
            className="cursor-pointer fill-brand-sand/0 stroke-brand-darkSage/40 hover:stroke-brand-gold transition-all" 
            strokeWidth="2" 
            cx={getPosX(i)} 
            cy={getPosY()} 
            r={circleRadius + 2} 
          />
        </g>
      ))}
    </svg>
  );
}